/**
 * C-GTCC-VIEW-DIAG-AIA1 — the "Berechtigungs-Prüfer" of the reference
 * (`GTCC_REFERENCE_2026-09-01.html:660`): "Rolle wählen → zeigt je Fläche
 * LESEN/SCHREIBEN/NICHTS".
 *
 * There is no HTTP route for this probe — `services/api/src` exposes no
 * permission-diagnostics endpoint. The answer is nevertheless *measured*, not
 * guessed: `apps/admin/src/access/roles.ts` is the role catalog that mirrors
 * `services/api/src/entitlements.ts` byte-for-byte, kept in step by
 * `roles.byte-equality.test.ts`. Reading the verdict out of that catalog is
 * therefore the same statement the server would make, and it needs no request.
 *
 * Pure and network-free by construction: this module imports no client and no
 * query. The reference's "0 Schreibrechte (403-bewiesen)" is a claim about a
 * live 403 that this repository cannot produce, so the view labels the result
 * as "aus dem Rollen-Katalog" and never as proven by a request.
 */
import { BUILT_IN_ROLES, permissionsForRole, type Permission } from "../../../access/roles.js";

/** LESEN / SCHREIBEN / NICHTS of the reference, in that vocabulary. */
export type PermissionAccess = "write" | "read" | "none";

export interface PermissionAreaDef {
  /** Stable id; the i18n key is `admin.gtcc.diag.perm.area.<id>`. */
  readonly id: string;
  readonly read: readonly Permission[];
  readonly write: readonly Permission[];
}

/**
 * The areas of the product, one per permission prefix of the `Permission`
 * union in `access/roles.ts`. The list is exhaustive over that union — the
 * test pins that every permission appears in exactly one area, so a new
 * entitlement cannot slip past this table unnoticed.
 *
 * `reporting`, `trip` and `audit` carry no write permission at all; their
 * `write` list is empty, which makes "SCHREIBEN" unreachable for them by
 * construction rather than by omission.
 */
export const PERMISSION_AREAS: readonly PermissionAreaDef[] = [
  { id: "fleet", read: ["fleet.read"], write: ["fleet.write", "fleet.admin"] },
  { id: "vehicle", read: ["vehicle.read"], write: ["vehicle.write"] },
  // G-W4-RECHTE1 — `driver.field_mode` is the driver's own field surface, not
  // a write on driver records, so it reads with the area rather than adding a
  // second write path to it.
  { id: "driver", read: ["driver.read", "driver.field_mode"], write: ["driver.write"] },
  // C-FUEL-DRIVER-STATEMENT1 — the driver's own review-case statement. The
  // self-scope is a server-side narrowing against `drivers.user_sub`, not a
  // separate area of the product, so the verdict here reads LESEN/SCHREIBEN
  // for the area exactly as the catalog grants it.
  { id: "fuel", read: ["fuel.statement.self.read"], write: ["fuel.statement.self.write"] },
  { id: "dispatch", read: ["dispatch.read"], write: ["dispatch.write"] },
  { id: "telemetry", read: ["telemetry.read"], write: ["telemetry.write"] },
  { id: "reporting", read: ["reporting.read"], write: [] },
  { id: "trip", read: ["trip.read"], write: [] },
  { id: "geofence", read: ["geofence.read"], write: ["geofence.write"] },
  { id: "device", read: ["device.read"], write: ["device.write"] },
  { id: "visibility", read: ["visibility.read"], write: ["visibility.write"] },
  {
    id: "tenant",
    read: ["tenant.read", "tenant.members.read"],
    write: ["tenant.admin", "tenant.members.write"],
  },
  { id: "billing", read: ["billing.read"], write: ["billing.admin"] },
  { id: "audit", read: ["audit.read"], write: [] },
  {
    id: "platform",
    read: ["platform.tenants.read"],
    // `platform.sims.manage` is a managing capability over the SIM inventory;
    // it belongs on the write side of the platform area.
    write: ["platform.tenants.write", "platform.sims.manage"],
  },
];

export interface PermissionAreaVerdict {
  readonly areaId: string;
  readonly access: PermissionAccess;
  /** The permissions of the catalog that produced this verdict, sorted. */
  readonly granted: readonly Permission[];
}

export interface PermissionRoleCheck {
  readonly roleId: string;
  readonly roleLabel: string;
  readonly areas: readonly PermissionAreaVerdict[];
  readonly writeAreaCount: number;
  readonly readOnlyAreaCount: number;
}

/** The roles the picker offers, in the catalog's own order. */
export const PERMISSION_ROLE_IDS: readonly string[] = Object.keys(BUILT_IN_ROLES);

export function permissionRoleLabel(roleId: string): string | null {
  const role = (BUILT_IN_ROLES as Record<string, { label: string } | undefined>)[roleId];
  return role === undefined ? null : role.label;
}

/**
 * Runs the check for one role. Returns `null` for an unknown role id — an
 * empty matrix would read as "this role may do nothing", which is a different
 * and false statement.
 */
export function checkPermissionRole(roleId: string): PermissionRoleCheck | null {
  const label = permissionRoleLabel(roleId);
  if (label === null) return null;

  const held = new Set<Permission>(permissionsForRole(roleId));
  const areas = PERMISSION_AREAS.map((area): PermissionAreaVerdict => {
    const granted = [...area.write, ...area.read].filter((permission) => held.has(permission));
    const access: PermissionAccess = area.write.some((permission) => held.has(permission))
      ? "write"
      : area.read.some((permission) => held.has(permission))
        ? "read"
        : "none";
    return { areaId: area.id, access, granted: [...granted].sort() };
  });

  return {
    roleId,
    roleLabel: label,
    areas,
    writeAreaCount: areas.filter((area) => area.access === "write").length,
    readOnlyAreaCount: areas.filter((area) => area.access === "read").length,
  };
}
